/*
 * collection-scroll-restore.js - revision 3, 2026-09-11 (+ counter fix 2026-10-07, see appendFrom;
 *   + normalised key and _su_rec cleanup 2026-10-07 fix2, see storageKey;
 *   + brand links save too, 2026-10-07 brands fix1, see LEAVE)
 *
 * Come back from a product page to the exact spot in a collection or a search grid,
 * including the items that were loaded by "show more" / infinite scroll.
 *
 * ---------------------------------------------------------------------------------------
 * WHY REVISION 3 - three holes, none of which a desktop harness can reach
 *
 * A. THE PAGE THAT NEVER RELOADS (the iPhone case).
 *    Revision 2 hung everything on one 'load' listener. iOS Safari answers the back button
 *    from the back/forward cache: the document is resumed from memory, so 'load' never
 *    fires again and restore() never runs. Worse, the click handler had already set
 *    history.scrollRestoration = 'manual' on that entry, so the browser's own restore was
 *    switched off too. Both mechanisms off at once = the visitor lands at the top of the
 *    grid, which is a regression against the behaviour Michael reports as working on his
 *    iPhone today, on a shop where ~82% of search traffic is mobile.
 *    Fix: 'pageshow' is a trigger alongside 'load'.
 *
 * B. scrollRestoration COULD STAY 'manual' FOR THE LIFE OF THE TAB.
 *    It was handed back only inside done(). Every early return - no key, a key that failed
 *    to parse, and now a key that aged out - left that history entry with the browser's
 *    scroll restore switched off and nothing of ours left to replace it.
 *    Fix: releaseScrollControl() on every exit, not only the happy one.
 *
 * C. NO AGE CHECK. saved.ts was written in revision 1 and never read once. See MAX AGE.
 *
 * ---------------------------------------------------------------------------------------
 * RUNNING EXACTLY ONCE PER RETURN
 * On a back-navigation that really re-fetched the document, BOTH 'load' and 'pageshow'
 * fire. Two independent layers stop the second from doing anything, and neither of them is
 * an assumption about which fires first:
 *   1. `running` - a real flag, raised before the first await and lowered in done().
 *   2. the key is one-shot - ssDel() runs synchronously, before any fetch starts.
 * The flag is lowered again in done() on purpose: one document can be resumed from bfcache
 * many times in a row (grid -> product -> back -> product -> back), and every return has to
 * get a fresh run.
 *
 * NOTHING IS RE-FETCHED WHEN IT IS ALREADY THERE
 * A resumed document still holds its 100 items. The decision to fetch is taken from the DOM
 * - needMore() compares the pager's next page and the live item count against the key - and
 * never from how we arrived. So the bfcache path finds the saved card already present,
 * reports 'card-on-page-1' and only scrolls. No second fetch, no doubled grid.
 *
 * WHERE THE SAVE HAPPENS
 * On the product-link click, capture phase. Not on 'beforeunload': iOS Safari fires it
 * unreliably, and a page entering bfcache is not supposed to get it at all. The click is
 * also the only moment the clicked card's identity is knowable. 'pagehide' is here for one
 * narrow job - re-asserting 'manual' while a key is pending, and recording whether this
 * document actually went into bfcache (stats.hidPersisted, the only in-page evidence of it).
 *
 * NO 'popstate' ON PURPOSE. popstate is for same-document history moves. On this template
 * those are the theme's own filter/pager pushState, and running a restore there would fight
 * the theme while it re-renders the grid. A click on a product card is a document
 * navigation, so coming back from one always surfaces as 'load' or 'pageshow'.
 *
 * ---------------------------------------------------------------------------------------
 * WHAT REVISION 2 FIXED, STILL TRUE
 * - Load first, scroll last. Nothing scrolls until every missing page is in the DOM.
 *   (Revision 1 jumped out of restore() before the loading loop and scrolled to a Y that
 *   belonged to a 100-item grid on a grid that held 50 - the footer bug.)
 * - Pages come back by fetch() ONLY. No synthetic click on the "show more" anchor: that was
 *   the revision-0 regression, <a href="?page=N"> navigated the whole document. No URL is
 *   built by hand either - we read the href the server just gave us and replace the pager
 *   with the fresh one, the way halo.ajaxInfiniteScrollingMapData does.
 * - Every append is de-duplicated by product handle, so page 1 is never overwritten and a
 *   race with the theme's own scroll-triggered loader cannot double the grid.
 * - Graceful failure: if the pages cannot be restored we scroll to the TOP OF THE GRID,
 *   never to the saved Y. A Y that falls past the end of the grid is discarded.
 * - The grid is ul#main-collection-product-grid > li.product on collection AND search pages
 *   ('.products-grid' matches only the search drawer - do not use it).
 * - li.product itself carries no data attributes; data-product-id / data-product-handle sit
 *   on div.product-item inside it.
 * - sessionStorage throws in Safari with "block all cookies"; every access is wrapped.
 *
 * Debug: add ?sp_debug=1 (stripped from the storage key, so a debug reload still finds a key
 * saved without it). window.__spScrollRestore.stats holds the outcome of the last run, and
 * .maxAgeMs is writable so a test can force the age check to bite.
 */
(function () {
  'use strict';

  var GRID = '#main-collection-product-grid';
  var ITEM = 'li.product';
  var MORE = '.pagination-infinite [data-infinite-scrolling]';
  var PAGER = '.pagination-wrapper';
  // LEAVE - the links inside the grid that take the visitor to another document and from which
  // she comes back with the back button. The product link was the only one until the brand
  // line arrived on the card (2026-10-07): a tap on the brand name after the second batch came
  // back to 50 cards and the footer, because nothing had been saved. Same save, same key:
  //   - .shc-brand a            the brand name on a card (snippets/product-card);
  //   - .shc-bstrip a.shb-chip--all   "all brands" at the end of the brand strip (no card: Y only).
  // The brand chips of the strip ([data-shb-chip]) are NOT here: they filter in place.
  var LEAVE = 'a[href*="/products/"], .shc-brand a, .shc-bstrip a.shb-chip--all';
  var MAX_FETCH = 8;            // hard cap: 8 extra pages (~450 products) per restore
  var FETCH_TIMEOUT = 9000;     // ms per page

  // MAX AGE - 60 minutes. sessionStorage already dies with the tab, so what this guards
  // against is a tab iOS Safari keeps alive for days. 60 min covers the real interruption
  // (trying the item on, a phone call, a meal) with margin, and still expires inside one
  // sitting, so a next-morning tab restore can never land on a position taken from a
  // catalogue that has since turned over - which on a one-of-a-kind second-hand shop is
  // the whole risk: item #50 tomorrow is a different item.
  var DEFAULT_MAX_AGE_MS = 60 * 60 * 1000;

  var DEBUG = /[?&]sp_debug=1/.test(location.search);

  var api;                      // assigned at the bottom; read for the live maxAgeMs
  var running = false;          // one restore at a time, one per return. A flag, not a hope.

  var stats = {
    fetches: 0, added: 0, failed: 0,
    outcome: 'idle', landing: 'none',
    trigger: 'none', ageMs: null, runs: 0, ignored: 0, hidPersisted: null
  };

  // Mutated in place, never reassigned: window.__spScrollRestore.stats holds this reference.
  function resetStats(trigger) {
    stats.fetches = 0;
    stats.added = 0;
    stats.failed = 0;
    stats.outcome = 'idle';
    stats.landing = 'none';
    stats.ageMs = null;
    stats.trigger = trigger || 'unknown';
    stats.runs++;
  }

  function log() {
    if (DEBUG && window.console) console.log.apply(console, ['[scroll-restore]'].concat([].slice.call(arguments)));
  }

  // ---- storage: a blocked sessionStorage must not kill the feature ----
  function ssGet(k) { try { return window.sessionStorage.getItem(k); } catch (e) { return null; } }
  function ssSet(k, v) { try { window.sessionStorage.setItem(k, v); return true; } catch (e) { log('storage write blocked', e && e.name); return false; } }
  function ssDel(k) { try { window.sessionStorage.removeItem(k); } catch (e) {} }

  // ---- who owns the scroll on the way back ----
  function takeScrollControl() {
    if ('scrollRestoration' in history) {
      try { history.scrollRestoration = 'manual'; } catch (e) {}
    }
  }

  // Hand the browser its own scroll restore back. Called on EVERY exit, including the early
  // ones. An entry left on 'manual' with no key left to act on has no scroll restore at all
  // for the rest of the tab's life, and that is a bug of its own.
  function releaseScrollControl() {
    if ('scrollRestoration' in history) {
      try { history.scrollRestoration = 'auto'; } catch (e) {}
    }
  }

  function grid() { return document.querySelector(GRID); }
  function moreLink() { return document.querySelector(MORE); }
  function itemCount() { var g = grid(); return g ? g.querySelectorAll(ITEM).length : 0; }

  // A collection-like page is one that actually has the grid. Do not sniff type=product:
  // #search-form-mobile does not send it.
  function isCollectionLike() {
    if (!/\/collections\/|\/search/.test(location.pathname)) return false;
    return !!grid();
  }

  // THE KEY IS BUILT FROM A NORMALISED ADDRESS (fix2, 2026-10-07).
  // On the way back from a product, Shopify can bounce the document request through
  // shop.app/accounts/sur and return it with &_su_rec=...&_su_rec_id=... appended. The key was
  // built from the raw search string, so the key saved before leaving was never found again
  // (scrollY 10,331 -> 0 after an in-place filter). normSearch() is the ONE function used by
  // the save (click), the read (load / pageshow) and pagehide, so they cannot drift apart:
  //   - page and sp_debug are dropped (the key must not move when pages are added);
  //   - _su_rec, _su_rec_id, preview_theme_id and click/campaign trackers are dropped: none
  //     of them changes the grid, and preview and live must behave the same;
  //   - what is left is sorted, so the same filters in another order give the same key.
  var KEY_DROP = { page: 1, sp_debug: 1, _su_rec: 1, _su_rec_id: 1, preview_theme_id: 1, fbclid: 1, gclid: 1 };
  function normSearch(search) {
    var out = [];
    try {
      new URLSearchParams(search || '').forEach(function (v, k) {
        if (KEY_DROP[k] || k.indexOf('utm_') === 0) return;
        out.push(encodeURIComponent(k) + '=' + encodeURIComponent(v));
      });
    } catch (e) {}
    return out.sort().join('&');
  }
  function normPath(path) {
    try { return decodeURIComponent(path); } catch (e) { return path; }
  }
  function keyFor(path, search) {
    var q = normSearch(search);
    return 'collection-scroll:' + normPath(path) + (q ? '?' + q : '');
  }
  function storageKey() { return keyFor(location.pathname, location.search); }

  // Take _su_rec / _su_rec_id out of the address bar, and nothing else: the rest of the query
  // string is kept byte for byte (no re-serialising), history.state is kept, no entry is added.
  // Otherwise the tail stays in the address and rides along into every filter URL after it.
  function stripSu(search) {
    var parts = String(search || '').replace(/^\?/, '').split('&').filter(function (kv) {
      var k = kv.split('=')[0];
      return kv !== '' && k !== '_su_rec' && k !== '_su_rec_id';
    });
    return parts.length ? '?' + parts.join('&') : '';
  }
  function cleanAddress() {
    try {
      var clean = stripSu(location.search);
      if (clean === location.search || !history.replaceState) return;
      history.replaceState(history.state, '', location.pathname + clean + location.hash);
      log('address cleaned');
    } catch (e) { log('address clean failed', e && e.message); }
  }

  function pageOf(url) { var m = /[?&]page=(\d+)/.exec(url || ''); return m ? parseInt(m[1], 10) : null; }

  // The page number the "show more" link points at = the first page NOT yet in the grid.
  function nextPage() {
    var l = moreLink();
    if (!l) return null;
    return pageOf(l.getAttribute('href') || l.getAttribute('data-href'));
  }

  function handleOf(li) {
    var a = li.querySelector('a[href*="/products/"]');
    if (!a) return null;
    var m = /\/products\/([^?#]+)/.exec(a.getAttribute('href') || '');
    if (!m) return null;
    try { return decodeURIComponent(m[1]).toLowerCase(); } catch (e) { return m[1].toLowerCase(); }
  }

  function presentHandles() {
    var seen = {};
    var g = grid();
    if (!g) return seen;
    var list = g.querySelectorAll(ITEM);
    for (var i = 0; i < list.length; i++) {
      var h = handleOf(list[i]);
      if (h) seen[h] = 1;
    }
    return seen;
  }

  function safeAttrValue(v) {
    return (typeof v === 'string' && v.indexOf('"') === -1 && v.indexOf('\\') === -1) ? v : null;
  }

  // The saved card, if it is already in the grid. Returns the li, not the inner node.
  function cardTarget(saved) {
    var el = null;
    var id = safeAttrValue(saved.productId);
    var handle = safeAttrValue(saved.productHandle);
    if (id) el = document.querySelector(GRID + ' [data-product-id="' + id + '"]');
    if (!el && handle) el = document.querySelector(GRID + ' [data-product-handle="' + handle + '"]');
    if (!el) return null;
    return (el.closest && el.closest(ITEM)) || el;
  }

  // ---- save ----
  document.addEventListener('click', function (e) {
    if (!e.target.closest) return;
    var a = e.target.closest(LEAVE);
    if (!a || !isCollectionLike()) return;
    var g = grid();
    if (!g || !g.contains(a)) return;
    var card = a.closest('[data-product-id],[data-product-handle]');
    var wrote = ssSet(storageKey(), JSON.stringify({
      y: window.scrollY,
      productId: card ? card.getAttribute('data-product-id') : null,
      productHandle: card ? card.getAttribute('data-product-handle') : null,
      nextPage: nextPage(),
      items: itemCount(),
      ts: Date.now()
    }));
    // Own the scroll on the way back. Without this the browser first restores a Y that
    // belongs to a grid we have not rebuilt yet, which is a flash of the footer, and it
    // also wakes the theme's scroll-triggered loader while we are fetching.
    if (wrote) takeScrollControl();
  }, true);

  // ---- loading: fetch only, never a document navigation ----
  function appendFrom(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var srcGrid = doc.querySelector(GRID);
    var cur = grid();
    if (!srcGrid || !cur) return 0;

    var seen = presentHandles();
    var added = 0;
    var kids = srcGrid.children;
    for (var i = 0; i < kids.length; i++) {
      var li = kids[i];
      if (!li.matches || !li.matches(ITEM)) continue;
      var h = handleOf(li);
      if (h && seen[h]) continue;          // no duplicates, and page 1 is never re-added
      cur.appendChild(document.importNode(li, true));
      if (h) seen[h] = 1;
      added++;
    }

    // Swap the pager so the next href advances by itself. If the new page has no pager we
    // have reached the end, and removing it stops the loop naturally.
    var newPager = doc.querySelector(PAGER);
    var curPager = document.querySelector(PAGER);
    if (curPager && curPager.parentNode) {
      if (newPager) curPager.parentNode.replaceChild(document.importNode(newPager, true), curPager);
      else curPager.parentNode.removeChild(curPager);
    }

    // The pager we just swapped in belongs to the LAST page fetched, so its counter reads
    // "51–100" while the grid holds 1–100. The theme's own loader writes 1 into
    // [data-total-start] after every append (theme.js, ajaxInfiniteScrollingMapData); do the
    // same here. Only when this document started on page 1 - a document opened directly on
    // ?page=N really does start at N's first item. (2026-10-07, category round, finding 7.)
    try {
      var firstPage = pageOf(location.search);
      if ((added || newPager) && (!firstPage || firstPage === 1)) {
        var starts = document.querySelectorAll('[data-total-start]');
        for (var s = 0; s < starts.length; s++) starts[s].textContent = '1';
      }
    } catch (e) { log('counter fix failed', e && e.message); }

    // What the theme does after its own append. lazysizes picks the new images up on its
    // own (it watches the DOM), and window.compare.show is false on this shop.
    try {
      if (added && window.wishlist && window.wishlist.show && window.sharedFunctions &&
          window.sharedFunctions.setLocalStorageProductForWishlist) {
        window.sharedFunctions.setLocalStorageProductForWishlist();
      }
    } catch (e) { log('wishlist re-init failed', e && e.message); }
    try {
      if (added && window.sharedFunctions && window.sharedFunctions.checkNeedToConvertCurrency &&
          window.sharedFunctions.checkNeedToConvertCurrency() && window.Currency) {
        var active = document.querySelector('#currencies .active');
        if (active) window.Currency.convertAll(window.shop_currency, active.getAttribute('data-currency'), 'span.money', 'money_format');
      }
    } catch (e) { log('currency re-init failed', e && e.message); }

    return added;
  }

  function fetchPage(url) {
    stats.fetches++;
    var ctrl = window.AbortController ? new AbortController() : null;
    var timer = ctrl ? setTimeout(function () { ctrl.abort(); }, FETCH_TIMEOUT) : null;
    var opts = { credentials: 'same-origin', headers: { 'X-Requested-With': 'XMLHttpRequest' } };
    if (ctrl) opts.signal = ctrl.signal;
    return fetch(url, opts)
      .then(function (r) { return r.ok ? r.text() : null; })
      .then(function (html) {
        if (timer) clearTimeout(timer);
        if (!html) { stats.failed++; return 0; }
        var n = appendFrom(html);
        stats.added += n;
        log('page loaded', url, '+' + n, 'total', itemCount());
        return n;
      })
      .catch(function (err) {
        if (timer) clearTimeout(timer);
        stats.failed++;
        log('fetch failed', url, err && err.name);
        return 0;
      });
  }

  // Asked of the DOM, not of the navigation type. This is what keeps a resumed bfcache
  // document - which still holds all 100 items - from fetching anything a second time.
  function needMore(saved) {
    var want = saved.nextPage;
    var cur = nextPage();
    if (want && cur && cur < want) return true;
    if (saved.items && itemCount() < saved.items) return true;
    return false;
  }

  function loadMissingPages(saved) {
    var tried = {};
    function step(n) {
      if (n >= MAX_FETCH) { stats.outcome = 'cap-reached'; return Promise.resolve(); }
      if (!needMore(saved)) { stats.outcome = 'pages-restored'; return Promise.resolve(); }
      var link = moreLink();
      if (!link) { stats.outcome = 'no-more-link'; return Promise.resolve(); }
      var url = link.getAttribute('href') || link.getAttribute('data-href');
      if (!url || tried[url]) { stats.outcome = 'pager-stuck'; return Promise.resolve(); }
      tried[url] = 1;
      // Mirror the theme's own guard so its scroll handler will not fetch the same page.
      if (link.classList) link.classList.add('is-loading');
      return fetchPage(url).then(function (added) {
        // If the pager was not replaced (a failed fetch), hand 'is-loading' back, or the
        // theme's scroll-triggered loader stays switched off for the rest of the page view.
        if (link.classList && link.isConnected) link.classList.remove('is-loading');
        if (!added) { stats.outcome = 'fetch-failed'; return; }
        if (cardTarget(saved)) { stats.outcome = 'card-found'; return; }
        return step(n + 1);
      });
    }
    return step(0);
  }

  // ---- scrolling, once and only at the end ----
  function gridBounds() {
    var g = grid();
    if (!g) return null;
    var r = g.getBoundingClientRect();
    return { top: r.top + window.scrollY, bottom: r.bottom + window.scrollY };
  }

  function finish(saved) {
    var card = cardTarget(saved);
    if (card) {
      card.scrollIntoView({ block: 'center' });
      stats.landing = 'card';
      return;
    }
    var b = gridBounds();
    // A saved Y that falls past the end of the grid is the footer bug. Discard it.
    if (typeof saved.y === 'number' && b && saved.y < b.bottom - 40) {
      window.scrollTo(0, saved.y);
      stats.landing = 'y';
      return;
    }
    if (b) {
      window.scrollTo(0, Math.max(0, b.top - 80));
      stats.landing = 'grid-top';
      return;
    }
    stats.landing = 'none';
  }

  // The single exit. Scrolls, gives the browser its scroll restore back, and lowers the
  // run flag so the next return from bfcache gets a clean run.
  function done(saved) {
    try { finish(saved); } catch (e) { log('finish failed', e && e.message); }
    releaseScrollControl();
    running = false;
    log('done', JSON.stringify(stats), 'items', itemCount());
  }

  function maxAge() {
    return (api && typeof api.maxAgeMs === 'number') ? api.maxAgeMs : DEFAULT_MAX_AGE_MS;
  }

  function restore(trigger) {
    if (running) { stats.ignored++; log('already running, ignoring', trigger); return; }
    // Not our page: touch nothing at all, not even scrollRestoration.
    if (!isCollectionLike()) return;

    var key = storageKey();
    var raw = ssGet(key);
    if (!raw) { releaseScrollControl(); return; }
    ssDel(key);                 // one shot, synchronous, before any fetch can start

    var saved = null;
    try { saved = JSON.parse(raw); } catch (e) {}
    if (!saved || (saved.y == null && !saved.productId && !saved.productHandle)) {
      releaseScrollControl();
      return;
    }

    resetStats(trigger);

    // A missing or non-numeric ts is a key from an older revision: treated as expired.
    // A negative age (the clock moved backwards) is treated as fresh.
    var age = (typeof saved.ts === 'number') ? (Date.now() - saved.ts) : Infinity;
    stats.ageMs = age;
    if (!(age <= maxAge())) {
      stats.outcome = 'expired';
      log('key too old', age, 'ms >', maxAge());
      releaseScrollControl();
      return;
    }

    running = true;
    log('restoring', trigger, saved, 'have', itemCount());

    try {
      // 'card-on-page-1' also covers the bfcache resume, where the card is present because
      // the document was never torn down. Kept under the old name: the tester's harness
      // asserts on this string.
      if (cardTarget(saved)) { stats.outcome = 'card-on-page-1'; done(saved); return; }
      if (!needMore(saved)) { stats.outcome = 'nothing-missing'; done(saved); return; }

      loadMissingPages(saved).then(function () { done(saved); }, function (err) {
        stats.outcome = 'loop-error';
        log('loop error', err);
        done(saved);
      });
    } catch (e) {
      // Never leave `running` raised or the entry on 'manual' because of a throw.
      stats.outcome = 'restore-threw';
      log('restore threw', e && e.message);
      done(saved);
    }
  }

  // ---- triggers ----

  // 1. 'load' - a plain load, and a back-navigation that really re-fetched the document.
  //    The grid and the pager are final by then.
  function onLoad() { setTimeout(function () { if (isCollectionLike()) cleanAddress(); restore('load'); }, 0); }
  if (document.readyState === 'complete') onLoad();
  else window.addEventListener('load', onLoad);

  // 2. 'pageshow' - the ONLY signal on a resume from the back/forward cache, where 'load'
  //    never fires again. event.persisted is the right test, but it is not dependable on
  //    every version, so the navigation type is kept as a second opinion. Both are harmless
  //    on a plain load: 'load' got there first and the key is one-shot.
  window.addEventListener('pageshow', function (e) {
    var navType = '';
    try {
      var entries = window.performance && window.performance.getEntriesByType &&
                    window.performance.getEntriesByType('navigation');
      if (entries && entries[0]) navType = entries[0].type || '';
    } catch (err) {}
    var persisted = !!(e && e.persisted === true);
    if (!persisted && navType !== 'back_forward') return;   // a plain first load: 'load' owns it
    setTimeout(function () {
      restore(persisted ? 'pageshow-bfcache' : 'pageshow-history');
    }, 0);
  });

  // 3. 'pagehide' - fires where iOS Safari's 'beforeunload' does not, and it is the only
  //    place that can tell us this document is being frozen rather than destroyed. It does
  //    not save (the click already did): it re-asserts 'manual' while a key is pending, and
  //    records e.persisted, which is the only evidence from inside the page that this
  //    document really went into bfcache.
  window.addEventListener('pagehide', function (e) {
    stats.hidPersisted = !!(e && e.persisted === true);
    if (!isCollectionLike()) return;
    if (!ssGet(storageKey())) return;   // nothing pending: leave the browser's setting alone
    takeScrollControl();
  });

  // Small read-only hook so a tester can assert the mechanism, not just the pixels.
  // maxAgeMs is writable on purpose, so a test can make the age check bite on demand.
  api = {
    stats: stats,
    maxAgeMs: DEFAULT_MAX_AGE_MS,
    key: storageKey,
    keyFor: keyFor,
    stripSu: stripSu,
    itemCount: itemCount,
    nextPage: nextPage,
    moreLink: moreLink,
    restore: restore,
    isRunning: function () { return running; },
    scrollRestoration: function () {
      return ('scrollRestoration' in history) ? history.scrollRestoration : 'unsupported';
    }
  };
  window.__spScrollRestore = api;
})();